import useBlurDataUrl from 'hooks/useBlurDataUrl';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

export interface IImage {
	src: string;
	alt: string;
	_width?: number;
	_height?: number;
	error_src?: string;
	object_fit?: string;
	lazy?: boolean;
	onError?: () => void;
	is_loading?: boolean;
	retry_count?: number;
	className?: string;
}

const CoreImg = (props: IImage) => {
	const { src, error_src, lazy, object_fit = 'cover', onError } = props;
	// loading-image-hook
	const [shimmer, toBase64] = useBlurDataUrl();
	const loading_src = `data:image/svg+xml;base64,${toBase64(shimmer())}`;

	// 이미지 경로
	const [img_src, setImgSrc] = useState(loading_src || src);

	const max_retry = props.retry_count ?? 5;

	// 화면 노출 여부
	const img_ref = useRef<HTMLImageElement>(null);
	const observer_ref = useRef<IntersectionObserver>();
	const [is_loaded, setIsLoaded] = useState(!lazy);
	const [retry_count, setRetryCount] = useState(props.retry_count ?? 0);

	const onIntersection = (
		entries: IntersectionObserverEntry[],
		io: IntersectionObserver,
	) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				io.unobserve(entry.target);
				setIsLoaded(true);
			}
		});
	};
	/** lazy 옵션이 true일 때 이미지 영역에 lazy 로딩 기능 추가 (IntersectionObserve) */
	useEffect(() => {
		if (!lazy) return;

		if (!observer_ref.current) {
			observer_ref.current = new IntersectionObserver(onIntersection);
		}
		img_ref.current && observer_ref.current.observe(img_ref.current);
	}, [img_ref]);

	/** 이미지가 로드되거나, 에러가 나면 상태에 맞는 이미지로 경로를 변경 */
	useEffect(() => {
		if (src === undefined || !is_loaded) return;

		const checkImageLoaded = () => {
			setImgSrc(loading_src);
			const img = new Image();
			img.src =
				src + (src.includes('?') ? `&cache=${retry_count}` : `?cache=${retry_count}`);
			img.onload = () => {
				setImgSrc(
					src + (src.includes('?') ? `&cache=${retry_count}` : `?cache=${retry_count}`),
				);
			};
			img.onerror = () => {
				if (retry_count >= max_retry) {
					setImgSrc(
						error_src ?? 'https://featuring-score-web.s3.amazonaws.com/media/NoImage.png',
					);
				} else {
					setRetryCount(retry_count + 1);
				}
				onError && onError();
			};
		};
		if (retry_count <= max_retry) {
			checkImageLoaded();
		}
	}, [src, is_loaded, retry_count]);

	useEffect(() => {
		if (props.is_loading) setImgSrc(loading_src);
	}, [props.is_loading]);

	return (
		<ProgressiveImg
			is_loading={img_src === loading_src || props?.is_loading === true}
			{...{ ...props, src: img_src }}
			ref={img_ref}
			draggable={false}
		/>
	);
};

const ProgressiveImg = styled.img<{
	is_loading: boolean;
	_width?: number;
	_height?: number;
	object_fit?: string;
}>`
	width: ${({ _width }) => (_width ? _width + 'px' : '100%')};
	height: ${({ _height }) => (_height ? _height + 'px' : '100%')};

	object-fit: ${({ object_fit }) => object_fit};
	object-position: center;

	${({ is_loading }) =>
		is_loading
			? css`
					filter: blur(10px);
					clip-path: inset(0);
			  `
			: css`
					filter: blur(0px);
					transition: filter 0.2s linear;
			  `}
`;

export default CoreImg;
